/**
 * 모니터링 벽의 끌어놓기 부품.
 *
 * 브라우저 기본 끌어놓기(`draggable`)를 쓰다가 포인터 끌기로 바꿨다. 기본 끌기는 반투명한
 * 잔상만 따라오고, 놓을 자리를 알리려면 `dragover` 마다 상태를 바꿔야 해서 카드 사이 틈을
 * 지날 때마다 표시가 켜졌다 꺼지며 벽 전체가 다시 그려졌다 — 끄는 손에는 **끊기는** 것으로
 * 느껴졌다. 손가락도 받지 않았다.
 *
 * 여기에는 그 끌기의 부품만 둔다: 따라다니는 카드(유령), 순서가 바뀔 때 카드가 미끄러져 가는
 * 움직임(FLIP), 가장자리 자동 스크롤, 끈 뒤의 클릭 막기. 무엇을 어디로 옮기는지는 벽이 정한다.
 */
import { useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { reorderItems } from '../content-rules.js';

/** 마우스는 이만큼 움직여야 끌기다 — 그보다 작은 떨림은 클릭이다. */
export const DRAG_THRESHOLD = 6;
/** 손가락은 이만큼 누르고 있어야 끌기다. 바로 끌리면 벽을 스크롤할 수 없다. */
export const TOUCH_HOLD_MS = 350;
/** 누르고 있는 동안 손가락이 이만큼 움직이면 끌기가 아니라 스크롤하려는 것으로 본다. */
export const TOUCH_SLOP = 8;

/**
 * `item` 을 `to` 자리로 옮긴 새 목록. 끄는 동안 미리 보여 줄 순서와 놓았을 때 저장할 순서가
 * 같은 규칙이어야 해서 한 곳에 둔다(순서 규칙 자체는 편성 목록과 같은 `reorderItems`).
 */
export function moveTo<T>(items: readonly T[], item: T, to: number): T[] {
  const from = items.indexOf(item);
  if (from === -1) return [...items];
  return reorderItems(items, from, Math.max(0, Math.min(items.length - 1, to)));
}

/** 원소별로 같은가. 이어 붙여 견주면 이름 속 구분자 때문에 다른 목록이 같게 뭉개진다. */
export function sameList<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value, at) => value === b[at]);
}

/** 놓을 자리를 셈할 때 쓰는 카드 한 장의 자리. */
export interface DropSlot {
  id: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * 포인터가 가리키는 **끼울 자리** — 카드 위가 아니라 카드 사이도 답이 된다.
 *
 * **예전에는 다른 카드 위에 정확히 올라가야만 순서가 바뀌었다.** 카드 사이의 틈, 줄 끝의
 * 빈 자리, 마지막 줄 아래는 아무 답도 주지 않아서, 옮기려는 사람은 "여기 놓고 싶은데 안
 * 잡히는" 자리를 손으로 더듬어 찾아야 했다 — 그것이 "자유롭지 않다"의 정체다.
 *
 * 격자는 줄로 나뉘어 있으므로 **세로로 줄을 먼저 고르고, 그 줄 안에서 가로로 자리를**
 * 고른다. 카드 가운데를 기준으로 왼쪽이면 그 앞, 오른쪽이면 그 뒤다. 줄보다 위면 첫 자리,
 * 아래면 끝자리 — 마지막 줄 아래 빈 곳에 놓아도 "맨 뒤"라는 뜻이 된다.
 *
 * `slots` 는 **끄는 카드를 뺀** 자리들이고, 돌려주는 값은 그 목록에 끼워 넣을 자리다.
 */
export function insertionIndex(slots: readonly DropSlot[], pointer: { x: number; y: number }): number {
  if (slots.length === 0) return 0;

  /* 줄로 묶는다 — 같은 줄은 세로 구간이 겹친다. 격자라 위에서 아래로 이미 정렬돼 있다. */
  const rows: DropSlot[][] = [];
  for (const slot of slots) {
    const row = rows.at(-1);
    const last = row?.at(-1);
    if (row && last && slot.top < last.bottom - (last.bottom - last.top) / 2) row.push(slot);
    else rows.push([slot]);
  }

  const row =
    rows.find((candidate) => pointer.y < (candidate[0]?.bottom ?? 0)) ?? (rows.at(-1) as DropSlot[]);
  const first = slots.indexOf(row[0] as DropSlot);

  for (let at = 0; at < row.length; at += 1) {
    const slot = row[at] as DropSlot;
    if (pointer.x < (slot.left + slot.right) / 2) return first + at;
  }
  return first + row.length;
}

/**
 * 끄는 동안 가장자리에서 스크롤할 속도(한 프레임에 px). 위로는 음수.
 *
 * 가장자리에 가까울수록 빠르다 — 일정한 속도면 긴 벽에서는 답답하고 짧게 옮길 때는 지나친다.
 * 영역은 보이는 높이의 1/3 을 넘지 않는다. 낮은 창에서 가운데까지 스크롤 영역이 되면 멈춰
 * 설 자리가 없다.
 */
export function edgeScrollSpeed(pointer: number, top: number, bottom: number, zone = 64, max = 20): number {
  const reach = Math.min(zone, (bottom - top) / 3);
  if (reach <= 0) return 0;
  if (pointer < top + reach) return -Math.ceil(max * Math.min(1, (top + reach - pointer) / reach));
  if (pointer > bottom - reach) return Math.ceil(max * Math.min(1, (pointer - (bottom - reach)) / reach));
  return 0;
}

/** 이 요소를 품은 세로 스크롤 영역. 없으면 null — 창 자체가 스크롤한다. */
export function scrollParentOf(node: HTMLElement | null): HTMLElement | null {
  for (let current = node?.parentElement ?? null; current && current !== document.body; current = current.parentElement) {
    const { overflowY } = getComputedStyle(current);
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) return current;
  }
  return null;
}

/**
 * 끌기를 놓은 직후의 클릭 한 번을 삼킨다.
 *
 * 누른 자리와 놓은 자리가 같은 조상 안이면 브라우저가 그 조상에 클릭을 보낸다. 지점 이름을
 * 잡고 끌어 놓았는데 그 지점이 접히면, 옮기려던 사람에게는 고장으로 보인다.
 */
export function suppressNextClick(): void {
  const swallow = (event: MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
}

/**
 * 모서리 끌기 — 카드 크기를 손으로 정하는 자리라면 어디서나 쓴다.
 *
 * 움직일 때마다 처음 잡은 자리에서 옮겨 간 거리로 `onMove` 를, 손을 떼면 `onEnd(true)`,
 * 끌기가 취소되면 `onEnd(false)` 를 부른다. 포인터가 카드를 벗어나도 따라가도록 창에 건다.
 *
 * **가로와 세로를 함께 준다.** 모니터링 판이 자유 배치가 되면서 카드는 높이도 사람이
 * 정하게 됐다. 폭만 쓰는 쪽(대시보드)은 둘째 인자를 받지 않으면 그만이다.
 */
export function dragCorner(
  event: ReactPointerEvent<HTMLElement>,
  onMove: (dx: number, dy: number) => void,
  onEnd: (commit: boolean) => void,
): void {
  const startX = event.clientX;
  const startY = event.clientY;
  const move = (moved: PointerEvent): void => onMove(moved.clientX - startX, moved.clientY - startY);
  const finish = (commit: boolean): void => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', cancel);
    onEnd(commit);
  };
  const up = (): void => finish(true);
  const cancel = (): void => finish(false);

  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', cancel);
}

/** 포인터를 따라다니는 카드. */
export interface Ghost {
  move(x: number, y: number): void;
  /**
   * 놓았다 — 내려앉을 자리가 있으면 그리로, 없으면 그 자리에서 사라진다. 다 내려앉으면
   * `done` 을 부른다(원래 카드를 그때 드러내면 같은 카드가 두 장 보이는 순간이 없다).
   */
  settle(landing: DOMRect | null, done?: () => void): void;
}

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 끄는 카드의 유령을 만든다.
 *
 * React 바깥(`document.body`)에 둔다 — 포인터가 움직일 때마다 React 상태를 바꾸면 벽 전체가
 * 다시 그려져 따라오는 카드가 뚝뚝 끊긴다. 위치는 프레임마다 한 번 `transform` 으로만 옮긴다.
 */
export function createGhost(source: HTMLElement, grab: { x: number; y: number }): Ghost {
  const rect = source.getBoundingClientRect();
  const node = document.createElement('div');
  node.className = 'wall-drag-ghost';
  node.setAttribute('aria-hidden', 'true');

  const body = source.cloneNode(true) as HTMLElement;
  body.classList.add('wall-drag-ghost-body');
  // 복제본이 원래 카드로 잘못 찾아지지 않게 표지를 뗀다.
  for (const marked of [body, ...Array.from(body.querySelectorAll<HTMLElement>('[data-flip],[id]'))]) {
    marked.removeAttribute('data-flip');
    marked.removeAttribute('id');
  }

  const width = rect.width;
  node.style.width = `${width}px`;
  node.style.height = `${rect.height}px`;
  node.appendChild(body);
  document.body.appendChild(node);

  // 잡은 자리가 포인터 밑에 머물게 한다.
  const offsetX = Math.max(12, Math.min(grab.x - rect.left, width - 12));
  const offsetY = grab.y - rect.top;

  let x = grab.x;
  let y = grab.y;
  let frame = 0;
  const place = (): void => {
    frame = 0;
    node.style.transform = `translate3d(${x - offsetX}px, ${y - offsetY}px, 0)`;
  };
  place();
  // 다음 프레임에 들어 올린다 — 같은 프레임에 붙이면 전환 없이 곧바로 떠 있는 모습이 된다.
  window.requestAnimationFrame(() => node.classList.add('is-lifted'));

  return {
    move(nextX, nextY) {
      x = nextX;
      y = nextY;
      if (!frame) frame = window.requestAnimationFrame(place);
    },
    settle(landing, done) {
      if (frame) {
        window.cancelAnimationFrame(frame);
        place();
      }
      node.classList.remove('is-lifted');
      const remove = (): void => {
        node.remove();
        done?.();
      };
      if (prefersReducedMotion()) {
        remove();
        return;
      }
      if (!landing) {
        node.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease-out' }).finished.then(remove, remove);
        return;
      }
      node
        .animate(
          [{ transform: node.style.transform }, { transform: `translate3d(${landing.left}px, ${landing.top}px, 0)` }],
          { duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'forwards' },
        )
        .finished.then(remove, remove);
    },
  };
}

/**
 * 순서가 바뀌면 자식들이 **새 자리로 미끄러져 간다**(FLIP).
 *
 * `data-flip` 이 붙은 직계 자식만 다룬다. 자리는 `offsetLeft/Top` 으로 잰다 — 움직이는 중인
 * 변환을 포함하지 않는 값이라 이전 자리와 새 자리를 곧바로 견줄 수 있다. 움직이던 도중에 또
 * 바뀌면 지금 보이는 자리에서 출발한다(끄는 동안 카드가 연달아 비켜서도 튀지 않는다).
 *
 * `key`(순서)가 바뀐 렌더에서만 움직인다. 크기를 바꿔 자리가 밀리는 것은 따라 움직일 일이
 * 아니다 — 손잡이를 끄는 동안 카드들이 출렁인다.
 */
export function useFlip(root: RefObject<HTMLElement | null>, key: string): void {
  const positions = useRef(new Map<string, { x: number; y: number }>());
  const lastKey = useRef(key);

  useLayoutEffect(() => {
    const container = root.current;
    if (!container) return;
    const animate = lastKey.current !== key && !prefersReducedMotion();
    lastKey.current = key;

    const next = new Map<string, { x: number; y: number }>();
    for (const child of Array.from(container.children)) {
      if (!(child instanceof HTMLElement) || !child.dataset.flip) continue;
      const at = { x: child.offsetLeft, y: child.offsetTop };
      next.set(child.dataset.flip, at);

      const before = positions.current.get(child.dataset.flip);
      if (!animate || !before) continue;
      const moving = getComputedStyle(child).transform;
      const shift = moving && moving !== 'none' ? new DOMMatrixReadOnly(moving) : null;
      const dx = before.x + (shift?.m41 ?? 0) - at.x;
      const dy = before.y + (shift?.m42 ?? 0) - at.y;
      for (const running of child.getAnimations()) if (running.id === 'wall-flip') running.cancel();
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;

      const motion = child.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
        duration: 220,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      });
      motion.id = 'wall-flip';
    }
    positions.current = next;
  });
}
